import { 
  CardLayout, 
  CardSection, 
  CardStyling, 
  CardType, 
  OperationalMode, 
  Environment,
  CardBaseLayout
} from '../../../types/compositeCards';
import { ArtifactProperty } from '../../../types/artifactItems';

export class CardLayoutFactory {
  /**
   * Create a card layout based on card type, UI role, model type, and artifact properties
   * Properties control what data is available and which sections should be displayed
   */
  static createLayout(
    cardType: CardType | string,
    uiRole: OperationalMode,
    modelType: Environment,
    properties?: ArtifactProperty[]
  ): CardLayout {
    const baseLayout = this.getBaseLayout(cardType, uiRole);
    const sections = this.getSections(cardType, uiRole, modelType, properties);
    const styling = this.getStyling(cardType, uiRole, modelType);

    return {
      baseLayout,
      sections,
      styling,
      maxWidth: this.getMaxWidth(cardType, uiRole),
      minHeight: this.getMinHeight(cardType, uiRole),
    };
  }

  /**
   * Get base layout for card type and UI role
   */
  private static getBaseLayout(cardType: CardType | string, uiRole: OperationalMode): CardBaseLayout {
    switch (uiRole) {
      case 'architect':
        return 'detailed';
      case 'operator':
        return 'compact';
      case 'coach':
        return 'expanded';
      case 'integrator':
        return 'detailed';
      case 'development':
        return 'detailed';
      case 'marketfit':
        return 'expanded';
      default:
        return 'expanded';
    }
  }

  /**
   * Get sections for card type, UI role, model type, and artifact properties
   * Properties determine what data is available and which sections to display
   */
  private static getSections(
    cardType: CardType | string,
    uiRole: OperationalMode,
    modelType: Environment,
    properties?: ArtifactProperty[]
  ): CardSection[] {
    const sections: CardSection[] = [];

    // If properties are provided, use them to determine sections
    if (properties && properties.length > 0) {
      return this.getSectionsFromProperties(cardType, uiRole, modelType, properties);
    }

    // Fallback to cardType-based logic for backward compatibility

    // Header section - always present
    sections.push({
      id: 'header',
      type: 'header',
      roleVisibility: ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'],
      modelVisibility: ['Prime', 'Company', 'Sandbox'],
      component: 'header',
      order: 1,
    });

    // Artifact card sections - ARCHITECT ROLE SIMPLIFIED VIEW
    // Skip default content section for architect artifact cards
    if (cardType === 'artifact' && uiRole === 'architect') {
      // For architect role: only show action component and metadata
      sections.push({
        id: 'artifact-action',
        type: 'custom',
        roleVisibility: ['architect'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'artifact-action',
        order: 3,
      });
      
      // Metadata section (collapsed by default)
      sections.push({
        id: 'metadata',
        type: 'metadata',
        roleVisibility: ['architect'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'metadata',
        order: 4,
        props: { defaultCollapsed: true }
      });
      
      // Skip other sections for architect role - they'll render in context panel
      return sections.sort((a, b) => a.order - b.order);
    }
    
    // Artifact card sections - COACH ROLE (for Organization assessment)
    if (cardType === 'artifact' && uiRole === 'coach') {
      // Assessment rank section
      sections.push({
        id: 'assessment-rank',
        type: 'custom',
        roleVisibility: ['coach'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'assessment-rank',
        order: 3,
      });
      
      // Child artifact list section
      sections.push({
        id: 'child-artifact-list',
        type: 'custom',
        roleVisibility: ['coach'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'child-artifact-list',
        order: 4,
      });
      
      // Action component for approval
      sections.push({
        id: 'artifact-action',
        type: 'custom',
        roleVisibility: ['coach'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'artifact-action',
        order: 5,
      });
      
      // Metadata section
      sections.push({
        id: 'metadata',
        type: 'metadata',
        roleVisibility: ['coach'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'metadata',
        order: 6,
        props: { defaultCollapsed: true }
      });
      
      return sections.sort((a, b) => a.order - b.order);
    }
    
    // Artifact card sections - OPERATOR ROLE (for Value Generation)
    if (cardType === 'artifact' && uiRole === 'operator') {
      // Value generation section (only for business_entity type)
      sections.push({
        id: 'value-generation',
        type: 'custom',
        roleVisibility: ['operator'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'value-generation',
        order: 3,
      });
      
      // Metadata section
      sections.push({
        id: 'metadata',
        type: 'metadata',
        roleVisibility: ['operator'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'metadata',
        order: 4,
        props: { defaultCollapsed: true }
      });
      
      return sections.sort((a, b) => a.order - b.order);
    }
    
    // Content section - for non-architect artifact cards and all other card types
    sections.push({
      id: 'content',
      type: 'content',
      roleVisibility: ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'],
      modelVisibility: ['Prime', 'Company', 'Sandbox'],
      component: 'content',
      order: 2,
    });
    
    // Artifact details section for OTHER roles (non-architect)
    if (cardType === 'artifact') {
      sections.push({
        id: 'artifact-details',
        type: 'custom',
        roleVisibility: ['operator', 'coach', 'integrator', 'development', 'marketfit'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'artifact-details',
        order: 3,
      });
    }

    // Artifact item details section for artifact_item cards
    if (cardType === 'artifact_item') {
      sections.push({
        id: 'artifact-item-detail',
        type: 'custom',
        roleVisibility: ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'artifact-item-detail',
        order: 3,
      });
    }

    // List-specific content for list cards
    if (cardType === 'list') {
      sections.push({
        id: 'list-content',
        type: 'custom',
        roleVisibility: ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'artifact-list', // Use the ArtifactList component
        order: 3,
      });
    }

    // Artifact Skills card sections
    if (cardType === 'artifact_skills') {
      sections.push({
        id: 'artifact-skills-content',
        type: 'content',
        roleVisibility: ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'artifact-skills',
        order: 2,
      });
    }

    // Process Skills card sections
    if (cardType === 'process_skills') {
      sections.push({
        id: 'process-skills-content',
        type: 'content',
        roleVisibility: ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'process-skills',
        order: 2,
      });
    }

    // Prime Artifact card sections
    if (cardType === 'prime_artifact') {
      sections.push({
        id: 'prime-artifact-content',
        type: 'content',
        roleVisibility: ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'],
        modelVisibility: ['Prime'],
        component: 'content',
        order: 2,
      });
    }

    // Actions section - role and type dependent (skip for list cards, skill cards, and prime_artifact cards)
    if (this.shouldShowActions(cardType, uiRole) && cardType !== 'list' && cardType !== 'artifact_skills' && cardType !== 'process_skills' && cardType !== 'prime_artifact') {
      sections.push({
        id: 'actions',
        type: 'actions',
        roleVisibility: this.getActionRoleVisibility(uiRole),
        modelVisibility: this.getActionModelVisibility(modelType),
        component: 'actions',
        order: 3,
      });
    }

    // Metadata section - role dependent (skip for list cards, skill cards, and prime_artifact cards)
    if (this.shouldShowMetadata(uiRole) && cardType !== 'list' && cardType !== 'artifact_skills' && cardType !== 'process_skills' && cardType !== 'prime_artifact') {
      sections.push({
        id: 'metadata',
        type: 'metadata',
        roleVisibility: this.getMetadataRoleVisibility(uiRole),
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'metadata',
        order: 4,
      });
    }

    // Role-specific sections
    const roleSections = this.getRoleSpecificSections(cardType, uiRole, modelType);
    sections.push(...roleSections);

    // Model-specific sections
    const modelSections = this.getModelSpecificSections(cardType, uiRole, modelType);
    sections.push(...modelSections);

    // Sort sections by order
    return sections.sort((a, b) => a.order - b.order);
  }

  /**
   * Get sections based on artifact properties
   * Properties control what data is available and which sections should be displayed
   */
  private static getSectionsFromProperties(
    cardType: CardType | string,
    uiRole: OperationalMode,
    modelType: Environment,
    properties: ArtifactProperty[]
  ): CardSection[] {
    const sections: CardSection[] = [];

    // Header section - always present
    sections.push({
      id: 'header',
      type: 'header',
      roleVisibility: ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'],
      modelVisibility: ['Prime', 'Company', 'Sandbox'],
      component: 'header',
      order: 1,
    });

    // Analyze properties to determine available data
    const hasArtifactScopeProperties = properties.some(p => p.property_scope === 'artifact');
    const hasArtifactItemScopeProperties = properties.some(p => p.property_scope === 'artifact_item');
    const hasChildReferences = properties.some(p => p.is_child_reference === true);
    const hasReferencedArtifacts = properties.some(p => p.input_source === 'referenced_artifact');
    const hasSystemGenerated = properties.some(p => p.input_source === 'system_generated');
    const hasUserInput = properties.some(p => p.input_source === 'user_input');

    // Content section - show if we have any properties
    if (properties.length > 0) {
      sections.push({
        id: 'content',
        type: 'content',
        roleVisibility: ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'content',
        order: 2,
      });
    }

    // Artifact details section - show if we have artifact-scoped properties
    if (hasArtifactScopeProperties && cardType === 'artifact') {
      sections.push({
        id: 'artifact-details',
        type: 'custom',
        roleVisibility: ['operator', 'coach', 'integrator', 'development', 'marketfit'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'artifact-details',
        order: 3,
      });
    }

    // Artifact item details section - show if we have artifact_item-scoped properties
    if (hasArtifactItemScopeProperties) {
      sections.push({
        id: 'artifact-item-detail',
        type: 'custom',
        roleVisibility: ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'artifact-item-detail',
        order: 3,
      });
    }

    // Child artifact list section - show if we have child references
    if (hasChildReferences) {
      sections.push({
        id: 'child-artifact-list',
        type: 'custom',
        roleVisibility: ['architect', 'operator', 'coach', 'integrator', 'development', 'marketfit'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'child-artifact-list',
        order: 4,
      });
    }

    // Value generation section - show for operator role if we have user input properties
    if (uiRole === 'operator' && hasUserInput && cardType === 'artifact') {
      sections.push({
        id: 'value-generation',
        type: 'custom',
        roleVisibility: ['operator'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'value-generation',
        order: 3,
      });
    }

    // Assessment rank section - show for coach role
    if (uiRole === 'coach' && cardType === 'artifact') {
      sections.push({
        id: 'assessment-rank',
        type: 'custom',
        roleVisibility: ['coach'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'assessment-rank',
        order: 3,
      });
    }

    // Artifact action section - show for architect and coach roles
    if ((uiRole === 'architect' || uiRole === 'coach') && cardType === 'artifact') {
      sections.push({
        id: 'artifact-action',
        type: 'custom',
        roleVisibility: ['architect', 'coach'],
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'artifact-action',
        order: 5,
      });
    }

    // Actions section - role and type dependent
    if (this.shouldShowActions(cardType, uiRole) && cardType !== 'list' && cardType !== 'artifact_skills' && cardType !== 'process_skills' && cardType !== 'prime_artifact') {
      sections.push({
        id: 'actions',
        type: 'actions',
        roleVisibility: this.getActionRoleVisibility(uiRole),
        modelVisibility: this.getActionModelVisibility(modelType),
        component: 'actions',
        order: 6,
      });
    }

    // Metadata section - role dependent
    if (this.shouldShowMetadata(uiRole) && cardType !== 'list' && cardType !== 'artifact_skills' && cardType !== 'process_skills' && cardType !== 'prime_artifact') {
      sections.push({
        id: 'metadata',
        type: 'metadata',
        roleVisibility: this.getMetadataRoleVisibility(uiRole),
        modelVisibility: ['Prime', 'Company', 'Sandbox'],
        component: 'metadata',
        order: 7,
        props: { defaultCollapsed: true }
      });
    }

    // Role-specific sections
    const roleSections = this.getRoleSpecificSections(cardType, uiRole, modelType);
    sections.push(...roleSections);

    // Model-specific sections
    const modelSections = this.getModelSpecificSections(cardType, uiRole, modelType);
    sections.push(...modelSections);

    // Sort sections by order
    return sections.sort((a, b) => a.order - b.order);
  }

  /**
   * Get styling for card type, UI role, and model type
   */
  private static getStyling(
    cardType: CardType | string,
    uiRole: OperationalMode,
    modelType: Environment
  ): CardStyling {
    const baseColor = this.getBaseColor(cardType, modelType);
    
    return {
      backgroundColor: 'white',
      borderColor: baseColor,
      borderWidth: '2px',
      borderRadius: '12px',
      boxShadow: `0 4px 6px ${baseColor}20`,
      color: '#1f2937',
    };
  }

  /**
   * Get base color for card type and model type
   */
  private static getBaseColor(cardType: CardType | string, modelType: Environment): string {
    if (modelType === 'Prime') {
      switch (cardType) {
        case 'artifact': return '#8b5cf6'; // Purple for Prime artifacts
        case 'prime_artifact': return '#4f46e5'; // Indigo for Prime Model artifacts
        case 'process': return '#3b82f6';  // Blue for Prime processes
        case 'artifact_item': return '#f59e0b'; // Orange for Prime items
        case 'list': return '#6b7280';     // Gray for Prime lists
        default: return '#8b5cf6';
      }
    } else if (modelType === 'Company') {
      switch (cardType) {
        case 'artifact': return '#00d455'; // Green for Company artifacts
        case 'process': return '#3b82f6';  // Blue for Company processes
        case 'artifact_item': return '#f59e0b'; // Orange for Company items
        case 'list': return '#6b7280';     // Gray for Company lists
        default: return '#00d455';
      }
    } else {
      // Sandbox
      switch (cardType) {
        case 'artifact': return '#f59e0b'; // Orange for Sandbox artifacts
        case 'process': return '#3b82f6';  // Blue for Sandbox processes
        case 'artifact_item': return '#ef4444'; // Red for Sandbox items
        case 'list': return '#6b7280';     // Gray for Sandbox lists
        default: return '#f59e0b';
      }
    }
  }

  /**
   * Check if actions should be shown for card type and UI role
   */
  private static shouldShowActions(cardType: CardType | string, uiRole: OperationalMode): boolean {
    // Always show actions for architect and integrator
    if (uiRole === 'architect' || uiRole === 'integrator') {
      return true;
    }

    // Show actions for operator on process and list cards
    if (uiRole === 'operator' && (cardType === 'process' || cardType === 'list')) {
      return true;
    }

    // Show actions for development role
    if (uiRole === 'development') {
      return true;
    }

    return false;
  }

  /**
   * Get role visibility for actions section
   */
  private static getActionRoleVisibility(uiRole: OperationalMode): OperationalMode[] {
    switch (uiRole) {
      case 'architect':
        return ['architect', 'integrator', 'development'];
      case 'operator':
        return ['operator', 'integrator'];
      case 'coach':
        return ['coach'];
      case 'integrator':
        return ['architect', 'operator', 'integrator', 'development'];
      case 'development':
        return ['architect', 'development'];
      case 'marketfit':
        return ['marketfit'];
      default:
        return [uiRole];
    }
  }

  /**
   * Get model visibility for actions section
   */
  private static getActionModelVisibility(modelType: Environment): Environment[] {
    switch (modelType) {
      case 'Prime':
        return ['Prime', 'Sandbox'];
      case 'Company':
        return ['Company', 'Sandbox'];
      case 'Sandbox':
        return ['Prime', 'Company', 'Sandbox'];
      default:
        return [modelType];
    }
  }

  /**
   * Check if metadata should be shown for UI role
   */
  private static shouldShowMetadata(uiRole: OperationalMode): boolean {
    return uiRole === 'architect' || uiRole === 'integrator' || uiRole === 'development';
  }

  /**
   * Get role visibility for metadata section
   */
  private static getMetadataRoleVisibility(uiRole: OperationalMode): OperationalMode[] {
    switch (uiRole) {
      case 'architect':
        return ['architect', 'integrator', 'development'];
      case 'integrator':
        return ['architect', 'integrator', 'development'];
      case 'development':
        return ['architect', 'development'];
      default:
        return [uiRole];
    }
  }

  /**
   * Get role-specific sections
   */
  private static getRoleSpecificSections(
    cardType: CardType | string,
    uiRole: OperationalMode,
    modelType: Environment
  ): CardSection[] {
    const sections: CardSection[] = [];

    switch (uiRole) {
      case 'architect':
        sections.push({
          id: 'architect-controls',
          type: 'custom',
          roleVisibility: ['architect'],
          modelVisibility: ['Prime', 'Company'],
          component: 'architect-controls',
          order: 5,
        });
        break;
      case 'operator':
        sections.push({
          id: 'operator-monitoring',
          type: 'custom',
          roleVisibility: ['operator'],
          modelVisibility: ['Company'],
          component: 'operator-monitoring',
          order: 5,
        });
        break;
      case 'coach':
        sections.push({
          id: 'coach-insights',
          type: 'custom',
          roleVisibility: ['coach'],
          modelVisibility: ['Prime', 'Company'],
          component: 'coach-insights',
          order: 5,
        });
        break;
    }

    return sections;
  }

  /**
   * Get model-specific sections
   */
  private static getModelSpecificSections(
    cardType: CardType | string,
    uiRole: OperationalMode,
    modelType: Environment
  ): CardSection[] {
    const sections: CardSection[] = [];

    switch (modelType) {
      case 'Prime':
        sections.push({
          id: 'prime-development',
          type: 'custom',
          roleVisibility: ['architect', 'development'],
          modelVisibility: ['Prime'],
          component: 'prime-development',
          order: 6,
        });
        break;
      case 'Company':
        sections.push({
          id: 'company-operations',
          type: 'custom',
          roleVisibility: ['operator', 'integrator'],
          modelVisibility: ['Company'],
          component: 'company-operations',
          order: 6,
        });
        break;
      case 'Sandbox':
        sections.push({
          id: 'sandbox-experiments',
          type: 'custom',
          roleVisibility: ['architect', 'development', 'marketfit'],
          modelVisibility: ['Sandbox'],
          component: 'sandbox-experiments',
          order: 6,
        });
        break;
    }

    return sections;
  }

  /**
   * Get max width for card type and UI role
   */
  private static getMaxWidth(cardType: CardType | string, uiRole: OperationalMode): string {
    switch (uiRole) {
      case 'architect':
        return '800px';
      case 'operator':
        return '600px';
      case 'coach':
        return '700px';
      case 'integrator':
        return '800px';
      case 'development':
        return '800px';
      case 'marketfit':
        return '700px';
      default:
        return '600px';
    }
  }

  /**
   * Get min height for card type and UI role
   */
  private static getMinHeight(cardType: CardType | string, uiRole: OperationalMode): string {
    switch (uiRole) {
      case 'architect':
        return '200px';
      case 'operator':
        return '150px';
      case 'coach':
        return '180px';
      case 'integrator':
        return '200px';
      case 'development':
        return '200px';
      case 'marketfit':
        return '180px';
      default:
        return '150px';
    }
  }
}
